/**
 * Spaces Database Client
 *
 * Client-side helpers for loading and mutating Spaces.
 *
 * Primary responsibilities:
 * - Load Spaces through authenticated API routes
 * - Preserve request status for auth-aware UI handling
 *
 * Dependencies:
 * - Spaces API routes
 */

import { authenticatedFetch } from "@/lib/auth/authenticated-fetch"
import {
  notifySidebarSpaceCreated,
  notifySidebarSpaceUpdated
} from "@/lib/sidebar/sidebar-mutation-events"
import { Tables } from "@/supabase/types"
import { SpaceInsert, SpaceUpdate, SpaceWithMembers } from "@/types/spaces"

type ResponseErrorPayload = {
  error?: string | { message?: string }
  message?: string
}

export class SpaceRequestError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message)
    this.name = "SpaceRequestError"
  }
}

export const isSpaceAuthRequestError = (error: unknown) => {
  return (
    error instanceof SpaceRequestError &&
    (error.status === 401 || error.status === 403)
  )
}

const getResponseError = async (response: Response, fallback: string) => {
  const body = await response.text().catch(() => "")
  const trimmedBody = body.trim()

  if (!trimmedBody) {
    return fallback
  }

  let payload: ResponseErrorPayload | string

  try {
    payload = JSON.parse(trimmedBody) as ResponseErrorPayload | string
  } catch {
    return trimmedBody
  }

  if (typeof payload === "string" && payload.trim()) {
    return payload
  }

  if (payload && typeof payload === "object") {
    if (typeof payload.error === "string" && payload.error.trim()) {
      return payload.error
    }

    if (
      payload.error &&
      typeof payload.error === "object" &&
      typeof payload.error.message === "string" &&
      payload.error.message.trim()
    ) {
      return payload.error.message
    }

    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message
    }
  }

  return fallback
}

export const getSpacesByWorkspaceId = async (workspaceId: string) => {
  const response = await authenticatedFetch(
    `/api/spaces?workspace_id=${encodeURIComponent(workspaceId)}`,
    {
      cache: "no-store"
    }
  )

  if (!response.ok) {
    throw new SpaceRequestError(
      await getResponseError(response, "Failed to load spaces"),
      response.status
    )
  }

  return (await response.json()) as Tables<"spaces">[]
}

export const getSpaceById = async (spaceId: string) => {
  const response = await authenticatedFetch(`/api/spaces/${spaceId}`, {
    cache: "no-store"
  })

  if (!response.ok) {
    throw new SpaceRequestError(
      await getResponseError(response, "Failed to load space"),
      response.status
    )
  }

  return (await response.json()) as Tables<"spaces">
}

export const getSpaceWithMembers = async (
  spaceId: string
): Promise<SpaceWithMembers> => {
  const space = (await getSpaceById(spaceId)) as SpaceWithMembers

  return {
    ...space,
    member_count: space.members?.length || 0
  }
}

export const createSpace = async (space: SpaceInsert) => {
  const response = await authenticatedFetch("/api/spaces", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(space)
  })

  if (!response.ok) {
    throw new SpaceRequestError(
      await getResponseError(response, "Failed to create space"),
      response.status
    )
  }

  const createdSpace = (await response.json()) as Tables<"spaces">
  notifySidebarSpaceCreated(createdSpace)

  return createdSpace
}

export const updateSpace = async (spaceId: string, updates: SpaceUpdate) => {
  const response = await authenticatedFetch(`/api/spaces/${spaceId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(updates)
  })

  if (!response.ok) {
    throw new SpaceRequestError(
      await getResponseError(response, "Failed to update space"),
      response.status
    )
  }

  const updatedSpace = (await response.json()) as Tables<"spaces">
  notifySidebarSpaceUpdated(updatedSpace)

  return updatedSpace
}

export const deleteSpace = async (spaceId: string) => {
  let spaceSnapshot: Tables<"spaces"> | null = null

  try {
    spaceSnapshot = await getSpaceById(spaceId)
  } catch {
    spaceSnapshot = null
  }

  const response = await authenticatedFetch(`/api/spaces/${spaceId}`, {
    method: "DELETE"
  })

  if (!response.ok) {
    const fallback =
      response.status === 404
        ? "Space not found or you do not have permission to delete it"
        : "Failed to delete space"

    throw new SpaceRequestError(
      await getResponseError(response, fallback),
      response.status
    )
  }

  // Card/settings delete paths don't optimistically remove from the sidebar;
  // emit the same mutation event create/update use so shared spaces state clears.
  if (spaceSnapshot) {
    notifySidebarSpaceUpdated({
      ...spaceSnapshot,
      deleted_at: new Date().toISOString()
    })
  }
}

export const restoreSpace = async (spaceId: string) => {
  await updateSpace(spaceId, { deleted_at: null })
}

export const toggleSpaceFavorite = async (spaceId: string) => {
  const space = await getSpaceById(spaceId)
  return updateSpace(spaceId, { favorited: !space.favorited })
}

export const archiveSpace = async (spaceId: string) => {
  return updateSpace(spaceId, { archived: true })
}

export const unarchiveSpace = async (spaceId: string) => {
  return updateSpace(spaceId, { archived: false })
}

export const getArchivedSpaces = async (workspaceId: string) => {
  const response = await authenticatedFetch(
    `/api/spaces?workspace_id=${encodeURIComponent(workspaceId)}&archived=true`,
    {
      cache: "no-store"
    }
  )

  if (!response.ok) {
    throw new SpaceRequestError(
      await getResponseError(response, "Failed to load archived spaces"),
      response.status
    )
  }

  return (await response.json()) as Tables<"spaces">[]
}

export const searchSpaces = async (workspaceId: string, query: string) => {
  const params = new URLSearchParams({
    workspace_id: workspaceId,
    q: query
  })
  const response = await authenticatedFetch(`/api/spaces?${params.toString()}`, {
    cache: "no-store"
  })

  if (!response.ok) {
    throw new SpaceRequestError(
      await getResponseError(response, "Failed to search spaces"),
      response.status
    )
  }

  return (await response.json()) as Tables<"spaces">[]
}
